import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnChanges,
	OnInit,
	SimpleChanges
} from '@angular/core';


import { VgStates } from './videogular/VgStates';
import { VideogularComponent } from './videogular/Videogular.Component';

@Component({
	selector: 'vb-playerjs-receiver',
	changeDetection: ChangeDetectionStrategy.OnPush,
	template: ``,
	standalone: false
})
export class VbPlayerjsReceiverComponent implements OnInit, OnChanges {
	private isLoop: boolean;
	private playerjs: any;
	private receiver: any;

	@Input() public currentState: VgStates;
	@Input() public currentTime: number;
	@Input() public isComplete: boolean;

	constructor(
		private api: VideogularComponent
	) {}

	public ngOnInit(): void {
		// Short-circuit, if window is the top. Only needed if it's being framed.
		if (window === window.top) {
			return;
		}

		import(/* webpackChunkName: "player.js" */ '@vbrick/player.js')
			.then(playerjsExports => {
				this.playerjs = playerjsExports.default;
				this.receiver = new this.playerjs.Receiver();

				this.initReceiver();
			});
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (!this.receiver) {
			return;
		}

		if (changes.currentState) {
			this.onCurrentStateChange();
		}

		if (changes.currentTime) {
			this.onCurrentTimeChange();
		}

		if(changes.isComplete) {
			this.onIsCompleteChange();
		}
	}

	private initReceiver(): void {
		/**
		 * player.js methods
		 * https://github.com/embedly/player.js
		 */
		this.receiver.on(this.playerjs.METHODS.PLAY, () => {
			this.api.play();
			this.receiver.emit(this.playerjs.EVENTS.PLAY);
		});

		this.receiver.on(this.playerjs.METHODS.PAUSE, () => this.api.pause());

		this.receiver.ready();

		this.receiver.on(this.playerjs.METHODS.GETPAUSED, callback => callback(this.api.currentState !== VgStates.PLAY));

		this.receiver.on(this.playerjs.METHODS.MUTE, () => this.api.setVolume(0));

		this.receiver.on(this.playerjs.METHODS.UNMUTE, () => this.api.setVolume(1));

		this.receiver.on(this.playerjs.METHODS.GETMUTED, callback => callback(!this.api.volume));

		this.receiver.on(this.playerjs.METHODS.GETVOLUME, callback => callback(this.api.volume * 100));

		this.receiver.on(this.playerjs.METHODS.SETVOLUME, input => this.api.setVolume(input / 100));

		this.receiver.on(this.playerjs.METHODS.GETDURATION, callback => callback(this.api.totalTime / 1000));

		this.receiver.on(this.playerjs.METHODS.GETCURRENTTIME, callback => callback(this.api.currentTime / 1000));

		this.receiver.on(this.playerjs.METHODS.SETCURRENTTIME, input => this.api.seekTime(input));

		// these 2 we do not support for loop so we use for the test to pass
		this.receiver.on(this.playerjs.METHODS.GETLOOP, callback => callback(this.isLoop));

		this.receiver.on(this.playerjs.METHODS.SETLOOP, () => this.isLoop = true);
	}

	private onCurrentStateChange(): void {
		switch ( this.currentState ) {
			case VgStates.PLAY:
				this.receiver.emit(this.playerjs.EVENTS.PLAY);
				break;

			case VgStates.PAUSE:
			case VgStates.STOP:
				this.receiver.emit(this.playerjs.EVENTS.PAUSE);
				break;
		}
	}

	private onCurrentTimeChange(): void {
		this.receiver.emit(this.playerjs.EVENTS.TIMEUPDATE, {
			seconds: this.api.currentTime / 1000,
			duration: this.api.totalTime / 1000
		});
	}

	private onIsCompleteChange(): void {
		if (this.isComplete) {
			this.receiver.emit(this.playerjs.EVENTS.ENDED);
		}
	}
}
